<template>
  <div
    id="training-load"
    class="pa-4"
  >
    <view-page-header />

    <portal
      key="trainingLoadPortal"
      to="headerFilters"
    >
      <v-row
        key="trainingLoadPortalFilter"
        align="center"
        dense
      >
        <v-col
          v-if="currentTab === 'main'"
          cols="auto"
          class="pr-6"
        >
          <v-btn
            :text="mainAnalysis.view !== 'rpe'"
            class="text-none"
            :color="mainAnalysis.view === 'rpe' ? 'primary' : undefined"
            @click="mainAnalysis.view = 'rpe'"
          >
            {{ $t('training_load.main.load_analysis') }}
          </v-btn>
          <v-btn
            :text="mainAnalysis.view !== 'weekly'"
            class="text-none"
            :color="mainAnalysis.view === 'weekly' ? 'primary' : undefined"
            @click="mainAnalysis.view = 'weekly'"
          >
            {{ $t('training_load.main.weekly') }}
          </v-btn>
          <v-btn
            :text="mainAnalysis.view !== 'plannification'"
            class="text-none"
            :color="mainAnalysis.view === 'plannification' ? 'primary' : undefined"
            @click="mainAnalysis.view = 'plannification'"
          >
            {{ $t('training_load.main.planning') }}
          </v-btn>
        </v-col>
        <v-col
          v-else-if="currentTab === 'gps'"
          cols="auto"
          class="pr-6"
        >
          <v-btn
            :text="gpsLoadPlayers.type !== 'last7days'"
            class="text-none"
            :color="gpsLoadPlayers.type === 'last7days' ? 'primary' : undefined"
            @click="gpsLoadPlayers.type = 'last7days'"
          >
            {{ $t('card.team_average_pain.last_7_days') }}
          </v-btn>
          <v-btn
            :text="gpsLoadPlayers.type !== 'last30days'"
            class="text-none"
            :color="gpsLoadPlayers.type === 'last30days' ? 'primary' : undefined"
            @click="gpsLoadPlayers.type = 'last30days'"
          >
            {{ $t('training_load.gps.last30') }}
          </v-btn>
          <v-btn
            :text="gpsLoadPlayers.type !== 'wc'"
            class="text-none"
            :color="gpsLoadPlayers.type === 'wc' ? 'primary' : undefined"
            @click="gpsLoadPlayers.type = 'wc'"
          >
            {{ $t('table_headers.weekly_load.weekly_change') }}
          </v-btn>
          <v-btn
            :text="gpsLoadPlayers.type !== 'acwr'"
            class="text-none"
            :color="gpsLoadPlayers.type === 'acwr' ? 'primary' : undefined"
            @click="gpsLoadPlayers.type = 'acwr'"
          >
            {{ $t('card_title.acwr') }}
          </v-btn>
          <v-btn
            :text="gpsLoadPlayers.type !== 'mon'"
            class="text-none"
            :color="gpsLoadPlayers.type === 'mon' ? 'primary' : undefined"
            @click="gpsLoadPlayers.type = 'mon'"
          >
            {{ $t('table_headers.weekly_load.monotony') }}
          </v-btn>
        </v-col>
        <v-col
          v-else-if="currentTab === 'heartrate'"
          cols="auto"
          class="pr-6"
        >
          <v-btn
            :text="heartRateLoad.interval !== '1'"
            class="text-none"
            :color="heartRateLoad.interval === '1' ? 'primary' : undefined"
            @click="heartRateLoad.interval = '1'"
          >
            {{ $t('card.team_average_pain.yesterday') }}
          </v-btn>
          <v-btn
            :text="heartRateLoad.interval !== '7'"
            class="text-none"
            :color="heartRateLoad.interval === '7' ? 'primary' : undefined"
            @click="heartRateLoad.interval = '7'"
          >
            {{ $t('training_load.heart_rate.last7') }}
          </v-btn>
          <v-btn
            :text="heartRateLoad.interval !== '30'"
            class="text-none"
            :color="heartRateLoad.interval === '30' ? 'primary' : undefined"
            @click="heartRateLoad.interval = '30'"
          >
            {{ $t('training_load.heart_rate.last30') }}
          </v-btn>
        </v-col>
        <v-col
          cols="auto"
          class="text-subtitle-1 grey--text pr-2"
        >
          {{ $t('picker.date.select_date.title') }}
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            @click="date = $dayjs(date).subtract(1, 'day').format('YYYY-MM-DD')"
          >
            <v-icon small>
              fa fa-arrow-left
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-menu>
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="date ? $dayjs(date).format('ll') : ''"
                :label="$t('picker.date.title')"
                solo
                hide-details
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="date"
              :first-day-of-week="1"
              no-title
              :max="$dayjs().format('YYYY-MM-DD')"
            />
          </v-menu>
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            :disabled="$dayjs().isSame($dayjs(date), 'date')"
            @click="date = $dayjs(date).add(1, 'day').format('YYYY-MM-DD')"
          >
            <v-icon small>
              fa fa-arrow-right
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </portal>

    <div v-if="currentTab === 'main'">
      <div v-if="mainAnalysis.view === 'rpe'">
        <v-row class="mt-5">
          <v-col xl="8">
            <section>
              <line-chart
                :raw-data="teamStats.data"
                :chart-value-keys="[ { key: 'FRES', type: 'line', label: $t('table_headers.main_load.freshness') }, ...(showWellnessData ? [{ key: 'player_wellness', type: 'area', label: $t('card_title.player_wellness') }] : []), { key: 'DL', type: 'bar', label: $t('chart_legends.daily_load') }, { key: 'AL', type: 'bar', label: $t('chart_legends.acute_load') }, { key: 'CL', type: 'bar', label: $t('chart_legends.chronic_load') } ]"
                x-value="date"
                y-value-prefix="stats."
                :yaxis="[ { title: { text: $t('table_headers.main_load.freshness') }, opposite: true }, ...(showWellnessData ? [{ title: { text: $t('table_headers.weekly_load.wellness') }, opposite: true, max: 100 }] : []), { title: { text: $t('chart_legends.load') }, seriesName: 'DL' } ]"
                :legend="{ clusterGroupedSeries: false }"
                :plot-options="{ bar: { borderRadius: 5, columnWidth: '30%' } }"
                :colors="['#775DD0', '#FF4560', '#008FFB', '#00E396', '#FEB019']"
                :markers="{ colors: ['#775DD0', '#FF4560', '#008FFB', '#00E396', '#FEB019'], size: .001 }"
                :fill="{ type: ['solid', 'gradient', 'solid', 'solid', 'solid'], opacity: [1, 1, 0.5, 0.5, 0.5], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.55, opacityTo: 0.25 } }"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <div class="subtitle-2 grey--text text--grey-100">
                      {{ $t('card_title.team_internal_load_rpe') }}
                    </div>
                  </v-card-title>
                </template>
              </line-chart>
            </section>
          </v-col>
          <v-col xl="4">
            <section>
              <line-chart
                class="mb-5"
                :raw-data="teamStats.data"
                :chart-value-keys="[ 'ACWR' ]"
                x-value="date"
                y-value-prefix="stats."
                :yaxis="[ { title: { text: $t('table_headers.main_load.acwr') }, min: 0, max: 3}]"
                :annotations="{ position: 'back', yaxis: [ { y: 0.8, y2: 1.3, opacity: 0.5, borderColor: '#86CC8E', fillColor: '#86CC8E' }, { y: 0.6, y2: 0.8, opacity: 0.5, borderColor: '#F4A641', fillColor: '#F4A641' }, { y: 1.3, y2: 1.5, opacity: 0.5, borderColor: '#F4A641', fillColor: '#F4A641' } ] }"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <div class="subtitle-2 grey--text text--grey-100">
                      {{ $t('card_title.team_acwr_trend') }}
                    </div>
                  </v-card-title>
                </template>
              </line-chart>
            </section>
          </v-col>
        </v-row>

        <section class="training-load__table mb-6">
          <v-card>
            <v-card-title class="px-6 py-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('table_title.players') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-text-field
                    v-model="playerName"
                    solo
                    dense
                    :label="$t('table_headers.name')"
                    hide-details
                    clearable
                    style="width: 200px"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="pl-2"
                >
                  <v-select
                    v-model="playerPositions.selectedId"
                    solo
                    dense
                    :label="$t('individual_analysis.data.position')"
                    hide-details
                    clearable
                    item-value="id"
                    item-text="name"
                    :items="playerPositions.data"
                    style="width: 200px"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="pl-2"
                >
                  <v-select
                    v-model="playerStatuses.selectedId"
                    solo
                    dense
                    :label="$t('table_headers.player_overview.status')"
                    hide-details
                    clearable
                    :items="playerStatuses.data"
                    item-value="id"
                    item-text="name"
                    style="width: 200px"
                  />
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pa-0">
              <internal-load-table
                :data="filteredPlayers"
                :loading="players.loading"
                :max-values="players.maxValues"
                @tableRowClick="onPlayerRowClicked"
              />
            </v-card-text>
          </v-card>
        </section>

        <v-row v-if="showWellnessData">
          <v-col>
            <section class="fill-height">
              <team-pain-widget
                :today-stats="latestTeamStats"
                :yesterday-stats="yesterdayTeamStats"
              />
            </section>
          </v-col>
          <v-col>
            <section class="fill-height">
              <radar-chart
                :series="[ { name: $t('table_headers.player_overview.score'), data: [ latestTeamStats.FAT_perc || 0, latestTeamStats.GMS_perc || 0, latestTeamStats.MOO_perc || 0, latestTeamStats.STL_perc || 0 ] } ]"
                :xaxis="{ categories: [$t('table_headers.player_overview.fatigue'), $t('chart_legends.muscle_soreness'), $t('table_headers.player_overview.mood'), $t('table_headers.main_load.stress')] }"
                :yaxis="{ min: 0, max: 100, tickAmount: 5, labels: { offsetX: -13 }}"
                class="fill-height"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <div class="subtitle-2 grey--text text--grey-100">
                      {{ $t('card_title.team_fatigue_soreness_mood_stress') }}
                    </div>
                  </v-card-title>
                </template>
              </radar-chart>
            </section>
          </v-col>
          <v-col>
            <section class="fill-height">
              <line-chart
                :raw-data="teamStats.data"
                :chart-value-keys="[ { key: 'SLQ', label: $t('chart_legends.sleep_quality'), type: 'line' }, { key: 'SLD', label: $t('chart_legends.sleep_duration'), type: 'line' }, { key: 'DL', label: $t('chart_legends.daily_load'), type: 'bar' } ]"
                x-value="date"
                y-value-prefix="stats."
                :yaxis="[ { title: { text: 'SLQ' }}, { title: { text: 'SLD' }, opposite: true}, { title: { text: '' }, labels: { show:false }} ]"
                class="fill-height"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <div class="subtitle-2 grey--text text--grey-100">
                      {{ $t('card_title.team_sleep_quality') }}
                    </div>
                  </v-card-title>
                </template>
              </line-chart>
            </section>
          </v-col>
        </v-row>

        <section class="fill-height mt-6">
          <v-row>
            <v-col
              v-if="!showWellnessData"
              xl="4"
            >
              <section class="fill-height">
                <team-pain-widget
                  :today-stats="latestTeamStats"
                  :yesterday-stats="yesterdayTeamStats"
                />
              </section>
            </v-col>
            <v-col :xl="!showWellnessData ? 4 : 6">
              <scatter-chart
                class="fill-height"
                :series="players.data.map(p => {
                  return {
                    name: p.last_name + ', ' + p.first_name,
                    data: [
                      [ p.latest_stats['RPE_7'] || 0, p.latest_stats['TTM_7'] || 0 ]
                    ]
                  }
                })"
                :xaxis="{
                  tickAmount: 10,
                  min: 0,
                  max: 10
                }"
                :annotations="{
                  xaxis: [{
                    x: 5,
                    strokeDashArray: 0,
                    borderColor: '#008FFB',
                  }],
                  yaxis: [{
                    y: ((players.maxValues.TTM_7 || 0) - (players.minValues.TTM_7 || 0)) / 2,
                    strokeDashArray: 0,
                    borderColor: '#008FFB'
                  }]
                }"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <v-row
                      dense
                      align="center"
                    >
                      <v-col class="text-no-wrap">
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('card_title.team_rpe_vs_ttm_last_seven') }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-title>
                </template>
              </scatter-chart>
            </v-col>
            <v-col :xl="!showWellnessData ? 4 : 6">
              <scatter-chart
                class="fill-height"
                :series="players.data.map(p => {
                  return {
                    name: p.last_name + ', ' + p.first_name,
                    data: [
                      [ p.latest_stats['RPE_30'] || 0, p.latest_stats['TTM_30'] || 0 ]
                    ]
                  }
                })"
                :xaxis="{
                  tickAmount: 10,
                  min: 0,
                  max: 10
                }"
                :annotations="{
                  xaxis: [{
                    x: 5,
                    strokeDashArray: 0,
                    borderColor: '#008FFB',
                  }],
                  yaxis: [{
                    y: ((players.maxValues.TTM_30 || 0) - (players.minValues.TTM_30 || 0)) / 2,
                    strokeDashArray: 0,
                    borderColor: '#008FFB'
                  }]
                }"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <v-row
                      dense
                      align="center"
                    >
                      <v-col class="text-no-wrap">
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('card_title.team_rpe_vs_ttm_last_thirty') }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-title>
                </template>
              </scatter-chart>
            </v-col>
          </v-row>
        </section>
      </div>
      <div v-else-if="mainAnalysis.view === 'weekly'">
        <training-load-weekly-team-table :latest-team-stats="latestTeamStats" />
        <training-load-weekly-graphs :daily-team-stats="dailyTeamStats" />
      </div>
      <div v-else-if="mainAnalysis.view === 'plannification'">
        <training-load-planification
          :loading="teamStats.loading"
          :latest-team-stats="latestTeamStats"
          :players="players.data"
        />
      </div>
    </div>

    <div v-else-if="currentTab === 'gps'">
      <v-card>
        <v-card-title class="px-6 py-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('table_title.players') }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-text-field
                v-model="playerName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerPositions.selectedId"
                solo
                dense
                :label="$t('individual_analysis.data.position')"
                hide-details
                clearable
                item-value="id"
                item-text="name"
                :items="playerPositions.data"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerStatuses.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.status')"
                hide-details
                clearable
                :items="playerStatuses.data"
                item-value="id"
                item-text="name"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <variable-selector
                v-if="variables.data"
                key="GPS"
                :variables="variables.data"
                :dashboard="dashboard"
                category="GPS"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-0">
          <performance-gps-load-table
            :player-performance="filteredPlayers"
            :loading="players.loading"
            :max-values="players.maxValues"
            :type="gpsLoadPlayers.type"
            :table-headers="tableHeadersGPS"
          />
        </v-card-text>
      </v-card>
      <training-load-gps-graphs
        v-if="filteredPlayers.length > 0"
        :players="filteredPlayers"
        :type="gpsLoadPlayers.type"
        :max-values="players.maxValues"
      />
    </div>

    <div v-else-if="currentTab === 'heartrate'">
      <v-card>
        <v-card-title class="px-6 py-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('table_title.players') }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-text-field
                v-model="playerName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerPositions.selectedId"
                solo
                dense
                :label="$t('individual_analysis.data.position')"
                hide-details
                clearable
                item-value="id"
                item-text="name"
                :items="playerPositions.data"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerStatuses.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.status')"
                hide-details
                clearable
                :items="playerStatuses.data"
                item-value="id"
                item-text="name"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <variable-selector
                v-if="variables.data"
                key="HR"
                :variables="variables.data"
                :dashboard="dashboard"
                category="HR"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-0">
          <training-load-heart-rate-table
            :max-values="players.maxValues"
            :players="filteredPlayers"
            :interval="heartRateLoad.interval"
            :loading="players.loading"
            :table-headers="tableHeadersHR"
          />
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  CoachApiService,
  PlayerApiService,
  PlayerPositionApiService,
  PlayerStatusApiService,
  TeamApiService
} from '@/data/services'
import debounce from 'lodash/debounce'
import InternalLoadTable from '@/components/training-load/InternalLoadTable'
import ViewPageHeader from '@/components/ViewPageHeader'
import LineChart from '@/components/charts/LineChart'
import dayjs from 'dayjs'
import { processMaxValues, processMinValues, toCamelCase } from '@/utils/helpers'
import RadarChart from '@/components/charts/RadarChart'
import TeamPainWidget from '@/components/training-load/TeamPainWidget.vue'
import PerformanceGpsLoadTable from '@/components/performance/PerformanceGpsLoadTable.vue'
import TrainingLoadHeartRateTable from '@/components/training-load/TrainingLoadHeartRateTable.vue'
import TrainingLoadWeeklyTeamTable from '@/components/training-load/TrainingLoadWeeklyTeamTable.vue'
import TrainingLoadWeeklyGraphs from '@/components/training-load/TraningLoadWeeklyGraphs.vue'
import TrainingLoadGpsGraphs from '@/components/training-load/TrainingLoadGpsGraphs.vue'
import { orderBy } from 'lodash'
import TrainingLoadPlanification from '@/components/training-load/TrainingLoadPlanification.vue'
import { EventBus } from '@/plugins/bus'
import ScatterChart from '@/components/charts/ScatterChart.vue'
import { DashboardType } from '@/data/models/Dashboard'
import VariableSelector from '@/components/performance/VariableSelector.vue'

export default {
  name: 'TrainingLoadView',
  components: { VariableSelector, ScatterChart, TrainingLoadPlanification, TrainingLoadWeeklyTeamTable, TrainingLoadWeeklyGraphs, TrainingLoadGpsGraphs, TrainingLoadHeartRateTable, PerformanceGpsLoadTable, TeamPainWidget, RadarChart, LineChart, ViewPageHeader, InternalLoadTable },
  data: () => ({
    currentTab: 'main',
    date: dayjs().format('YYYY-MM-DD'),
    players: {
      data: [],
      loading: false,
      maxValues: {},
      minValues: {}
    },
    teamStats: {
      data: [],
      loading: false
    },
    dailyTeamStats: {
      data: [],
      loading: false
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerName: '',
    mainAnalysis: {
      view: 'rpe'
    },
    gpsLoadPlayers: {
      type: 'last7days'
    },
    heartRateLoad: {
      interval: '1'
    },
    variables: {
      data: null,
      loading: false
    },
    customizeModal: false,
    view: 'GPS',
    dashboard: DashboardType.TRAINING_LOAD
}),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM].id
    },
    latestTeamStats() {
      if (this.teamStats.data.length === 0) return {}

      return this.teamStats.data[this.teamStats.data.length - 1].stats
    },
    yesterdayTeamStats() {
      if (this.teamStats.data.length < 2) return {}

      return this.teamStats.data[this.teamStats.data.length - 2].stats
    },
    filteredPlayers() {
      return this.players.data.filter(player => {
        if (this.playerPositions.selectedId) return player.player_positions.map(p => p.id).indexOf(this.playerPositions.selectedId) > -1

        return player
      }).filter(player => {
        if (this.playerStatuses.selectedId) return player.player_status_id === this.playerStatuses.selectedId

        return player
      }).filter(player => {
        if (this.playerName) return (player.first_name + player.last_name).toLowerCase().includes(this.playerName.toLowerCase())

        return player
      })
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersHR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.HR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    category() {
      if (this.currentTab === 'gps') {
        return 'GPS'
      } else if (this.currentTab === 'heartrate') {
        return 'HR'
      }
      return ''
    }
  },
  watch: {
    currentTeamId() {
      this.fetchPlayers()
      this.fetchTeamStats()
      this.fetchDailyTeamStats()
    },
    date() {
      this.fetchPlayers()
      this.fetchTeamStats()
      this.fetchDailyTeamStats()
    },
    '$route.query.view': function () {
      this.currentTab = this.$route.query.view
    },
    currentTab: function (val) {
      if (val === 'gps') {
        this.view = 'GPS'
      } else if (val === 'heartrate') {
        this.view = 'HR'
      }
    }
  },
  created() {
    if (this.$route.query.view) this.currentTab = this.$route.query.view

    this.players.loading = true

    this.fetchVariables()
    this.fetchPlayers()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()

    if (this.currentTeamId) {
      this.fetchTeamStats()
      this.fetchDailyTeamStats()
    }

    EventBus.$on(this.$events.INJURY_CREATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_UPDATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_DELETED, () => {
      this.fetchPlayers()
    })
  },
  methods: {
    orderBy,
    onPlayerRowClicked(playerId) {
      this.$router.push({ name: this.$routeNames.PLAYER_DETAILS, params: { id: playerId } })
    },
    fetchPlayers: debounce(async function() {
      try {
        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeamId === 'all' ? undefined : this.currentTeamId,
          stats_date: this.date
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }

          if (player.latest_stats.SLD) player.latest_stats.SLD = (player.latest_stats.SLD / 60).toFixed(2)

          return player
        })

        this.players.maxValues = processMaxValues(players.map(player => player.latest_stats))
        this.players.minValues = processMinValues(players.map(player => player.latest_stats))

        this.players.data = players
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    async fetchTeamStats() {
      if (this.currentTeamId === 'all' || !this.currentTeamId) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.currentTeamId, dayjs.utc(this.date).subtract(7, 'days').toISOString(), dayjs.utc(this.date).toISOString())

        this.teamStats.data = teamStats.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    },
    async fetchDailyTeamStats() {
      if (this.currentTeamId === 'all' || !this.currentTeamId) return

      this.dailyTeamStats.loading = true

      try {
        const dailyTeamStats = await TeamApiService.getStats(this.currentTeamId, dayjs.utc(this.date).subtract(28, 'days').toISOString(), dayjs.utc(this.date).toISOString())

        this.dailyTeamStats.data = dailyTeamStats.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.daily_team_stats_pulling.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.dailyTeamStats.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        this.variables.data = await CoachApiService.getVariables(this.user.coach.id, {
          dashboard: this.dashboard
        })

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    yAxis(variable) {
      return [{
        y: ((this.players.maxValues[variable] || 0) - (this.players.minValues[variable] || 0)) / 2,
        strokeDashArray: 0,
        borderColor: '#008FFB'
      }]
    }
  }
}
</script>
